import {
  IonActionSheet,
  IonContent,
  IonFab,
  IonFabButton,
  IonFabList,
  IonIcon,
  IonImg,
  IonPage,
  useIonRouter,
} from '@ionic/react';
import React, { useCallback, useState } from 'react';

import { albums, colorPalette, settings } from 'ionicons/icons';

import Login from '../../components/Auth/Login';
import Password from '../../components/Auth/Password';
import Register from '../../components/Auth/Register';
import InitialSlides from '../../components/Otros/InitialSlides';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import Spinner from '../../components/UI/Spinner';
import { useThemeManager } from '../../theme/useThemeManager';
import { AuthMode } from '../../types/Auth/types';
import './AuthPage.scss';

const AuthPage: React.FC = () => {
  const { push } = useIonRouter();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [isSlidesModalOpen, setIsSlidesModalOpen] = useState<boolean>(false);

  const {
    theme,
    loading,
    isThemeSelectorOpen,
    openThemeSelector,
    closeThemeSelector,
    themeButtons,
    isDynamicThemeEnabled,
  } = useThemeManager();

  const handleSlidesClose = useCallback(() => setIsSlidesModalOpen(false), []);
  const handleSlidesOpen = () => {
    // Quitar focus del botón antes de abrir modal para evitar warning de aria-hidden
    (document.activeElement as HTMLElement)?.blur();
    setIsSlidesModalOpen(true);
  };
  const handleSetRegister = () => setAuthMode('register');
  const handleSetLogin = () => setAuthMode('login');
  const handleLoginSuccess = useCallback(() => {
    push('/tabs/home');
  }, [push]);

  const formContent = () => {
    const forms: Record<AuthMode, JSX.Element> = {
      login: (
        <>
          <Login setAuthMode={setAuthMode} onLoginSuccess={handleLoginSuccess} />
          <Button onClick={handleSetRegister} fill="clear">
            <div className="auth-page__secondary-btn">
              <span>¿No tenés una cuenta? </span>
              Registrate
            </div>
          </Button>
        </>
      ),
      register: (
        <>
          <Register setAuthMode={setAuthMode} />
          <Button onClick={handleSetLogin} fill="clear">
            <div className="auth-page__secondary-btn">
              <span>¿Tenés una cuenta? </span>
              Iniciá sesión
            </div>
          </Button>
        </>
      ),
      password: (
        <>
          <Password setAuthMode={setAuthMode} />
          <Button onClick={handleSetLogin} fill="clear">
            <div className="auth-page__secondary-btn">
              <span>Iniciar sesión</span>
            </div>
          </Button>
        </>
      ),
    };

    return forms[authMode] || null;
  };

  if (loading) return <Spinner fullscreen />;

  return (
    <IonPage className="auth-page">
      <IonContent>
        <div className="auth-page__content">
          {theme?.logoUrl && (
            <div className="auth-page__brand-img">
              <IonImg src={theme.logoUrl} alt={`${theme.brandName || 'Brand'} Logo`} />
            </div>
          )}

          <div className="auth-page__forms ion-padding">{formContent()}</div>
        </div>

        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          {isDynamicThemeEnabled ? (
            <>
              <IonFabButton color="primary">
                <IonIcon icon={settings} />
              </IonFabButton>
              <IonFabList side="top">
                <IonFabButton
                  color="dark"
                  onClick={handleSlidesOpen}
                  aria-label="Ver información"
                >
                  <IonIcon icon={albums} />
                </IonFabButton>
                <IonFabButton
                  color="dark"
                  onClick={openThemeSelector}
                  aria-label="Cambiar tema"
                >
                  <IonIcon icon={colorPalette} />
                </IonFabButton>
              </IonFabList>
            </>
          ) : (
            <IonFabButton color="primary" onClick={handleSlidesOpen} aria-label="Ver información">
              <IonIcon icon={albums} />
            </IonFabButton>
          )}
        </IonFab>

        {isDynamicThemeEnabled && (
          <IonActionSheet
            isOpen={isThemeSelectorOpen}
            onDidDismiss={closeThemeSelector}
            buttons={themeButtons}
          />
        )}

        <Modal isOpen={isSlidesModalOpen} onClose={handleSlidesClose} navigation={false}>
          <InitialSlides onClose={handleSlidesClose} />
        </Modal>
      </IonContent>
    </IonPage>
  );
};

export default AuthPage;